import {
  getIdTokenResult,
  signInWithCustomToken,
  signOut,
} from "firebase/auth";
import { auth } from "../Firebase/firebase";
import apiCall from "../api/Api";

// Bridges the Laravel Sanctum session into Firebase Auth so Firestore can
// authorise chat access per-thread instead of relying on world-open rules.
//
// Panel staff never sign in to Firebase directly. mohtaj-api mints a custom
// token from the Sanctum session (Admin SDK createCustomToken, POST
// chat/firebase-token) carrying the caller's uid and — for admins — the claim
// docs/firestore.rules keys admin access off. This module exchanges that token
// for a Firebase session and hands callers a promise they can gate Firestore
// work on.
//
// This mirrors src/services/FirebaseChatAuth.js in mohtaj-mobile and
// mohtaj-customer-app; keep the three in step. Until every client signs in the
// hardened ruleset cannot be deployed, because the clients that have not
// landed it would break the moment request.auth started being required.
const CHAT_TOKEN_ENDPOINT = "chat/firebase-token";

// Api.js's handleSuccess returns either the response body or the raw axios
// response depending on whether the body has a nested `data` key, so the token
// can legitimately arrive at several depths. Rather than pin one shape and
// break on the next backend tweak, look for a JWT-looking string under any of
// the names the endpoint has used.
const TOKEN_KEYS = ["firebase_token", "custom_token", "customToken", "token"];

export const pickCustomToken = (payload) => {
  const containers = [payload, payload?.data, payload?.data?.data];

  for (const container of containers) {
    if (!container || typeof container !== "object") continue;
    for (const key of TOKEN_KEYS) {
      const value = container[key];
      if (typeof value === "string" && value.length > 0) return value;
    }
  }

  return null;
};

// A signed-in Firebase user is not automatically the right one. Only a session
// minted by chat/firebase-token carries the uid and claims the rules match on;
// any other provider (a Google popup, a leftover session from another Mohtaj
// surface on this shared Firebase project) authenticates as somebody the rules
// will deny, and denials read as an empty inbox rather than as an error.
// signInProvider is "custom" only for signInWithCustomToken.
const isChatSession = async (user) => {
  try {
    const { signInProvider } = await getIdTokenResult(user);
    return signInProvider === "custom";
  } catch (error) {
    // Could not read the claims — treat the session as unusable and re-mint.
    return false;
  }
};

// One in-flight sign-in shared by every caller. Chat screens mount several
// listeners at once; without this each would mint its own token and race
// signInWithCustomToken against the others.
let inFlight = null;

export const ensureFirebaseChatAuth = async () => {
  // Firebase restores a persisted session asynchronously, so currentUser is
  // null for a tick after a page load even when the user is still signed in.
  // Waiting here avoids minting a fresh token on every refresh.
  await auth.authStateReady();

  if (auth.currentUser && (await isChatSession(auth.currentUser))) {
    return auth.currentUser;
  }
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const response = await apiCall("POST", CHAT_TOKEN_ENDPOINT);
    const customToken = pickCustomToken(response);

    if (!customToken) {
      throw new Error(
        `No Firebase custom token in the ${CHAT_TOKEN_ENDPOINT} response`
      );
    }

    const credential = await signInWithCustomToken(auth, customToken);
    return credential.user;
  })();

  try {
    return await inFlight;
  } finally {
    // Cleared on failure too, so a retry re-mints instead of replaying the
    // rejected promise forever.
    inFlight = null;
  }
};

// Called on logout. The Firebase session outlives the Sanctum one otherwise,
// leaving a signed-in chat identity behind on a shared machine.
export const resetFirebaseChatAuth = async () => {
  inFlight = null;
  try {
    await signOut(auth);
  } catch (error) {
    // Best effort — the caller is tearing the session down either way.
    console.error("Firebase chat sign-out failed:", error);
  }
};

export default ensureFirebaseChatAuth;
